import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// material-ui
import { useTheme } from "@mui/material/styles";
import {
  Avatar,
  Box,
  ButtonBase,
  CardActions,
  Chip,
  ClickAwayListener,
  Divider,
  Grid,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Popper,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";

// project imports
import MainCard from "ui-component/cards/MainCard";
import Transitions from "ui-component/extended/Transitions";
import RedBubble from "ui-component/RedBubble";
import { usePendingTasks } from "context/PendingTasksContext";

// assets
import {
  IconBell,
  IconCheck,
  IconRefresh,
  IconUserCheck,
  IconCalendarEvent,
  IconAlertCircle,
  IconAlertTriangle,
  IconUserExclamation,
  IconX,
} from "@tabler/icons";

const PendingTasksSection = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const matchesXs = useMediaQuery(theme.breakpoints.down("md"));
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);

  const userData = JSON.parse(window.localStorage.getItem("User State") || "{}");
  const role = parseInt(userData?.user?.role || userData?.role || 0);

  const { counts, totalPending, loading, refreshPendingTasks } = usePendingTasks();

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const prevOpen = useRef(open);
  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current?.focus();
    }
    prevOpen.current = open;
  }, [open]);

  // Navigate to task target
  const handleItemClick = (url) => {
    setOpen(false);
    navigate(url);
  };

  // Build task categories based on user role
  const isStudent = role === 4;

  const taskCategories = isStudent
    ? [
        {
          key: "studentLeaves",
          title: "My Pending Leaves",
          count: counts.studentLeaves || 0,
          url: "/userDashboard/manageLeave/manageLeave",
          icon: <IconCalendarEvent stroke={1.5} size="1.25rem" />,
          color: "#f59e0b",
        },
        {
          key: "grievances",
          title: "Grievances in Progress",
          count: counts.grievances || 0,
          url: "/userDashboard/grivance/myRaisedGrivance",
          icon: <IconAlertCircle stroke={1.5} size="1.25rem" />,
          color: "#ec4899",
        },
        {
          key: "discipline",
          title: "Disciplinary Actions",
          count: counts.discipline || 0,
          url: "/userDashboard/disciplinaryAction/myDisciplinaryActions",
          icon: <IconAlertTriangle stroke={1.5} size="1.25rem" />,
          color: "#ef4444",
        },
        {
          key: "profileVerification",
          title: "Profile Update Request",
          count: counts.profileVerification || 0,
          url: "/userDashboard/manageProfile",
          icon: <IconUserExclamation stroke={1.5} size="1.25rem" />,
          color: "#8b5cf6",
        },
        {
          key: "cancelAdmission",
          title: "Cancel Admission Request",
          count: counts.cancelAdmission || 0,
          url: "/userDashboard/cancelAdmission/allCancelAdmissionRequest",
          icon: <IconX stroke={1.5} size="1.25rem" />,
          color: "#64748b",
        },
      ]
    : [
        {
          key: "studentVerification",
          title: "Unverified Students",
          count: counts.studentVerification || 0,
          url: "/dashboard/verifyStudent/unverifiedStudents",
          icon: <IconUserCheck stroke={1.5} size="1.25rem" />,
          color: "#3b82f6",
        },
        {
          key: "profileVerification",
          title: "Profile Update Requests",
          count: counts.profileVerification || 0,
          url: "/dashboard/studentProfileManagement",
          icon: <IconUserExclamation stroke={1.5} size="1.25rem" />,
          color: "#8b5cf6",
        },
        {
          key: "studentLeaves",
          title: "Student Pending Leaves",
          count: counts.studentLeaves || 0,
          url: "/dashboard/leaveManagement/manageLeave",
          icon: <IconCalendarEvent stroke={1.5} size="1.25rem" />,
          color: "#f59e0b",
        },
        {
          key: "staffLeaves",
          title: "Staff Pending Leaves",
          count: counts.staffLeaves || 0,
          url: "/dashboard/staffLeaveManagement/manageLeaveRequest",
          icon: <IconCalendarEvent stroke={1.5} size="1.25rem" />,
          color: "#10b981",
        },
        {
          key: "grievances",
          title: "Pending Grievances",
          count: counts.grievances || 0,
          url: "/dashboard/grievanceManagementAdmin/allGrievanceRequest",
          icon: <IconAlertCircle stroke={1.5} size="1.25rem" />,
          color: "#ec4899",
        },
        {
          key: "discipline",
          title: "Disciplinary Actions",
          count: counts.discipline || 0,
          url: "/dashboard/disciplinaryAction/allDisciplinaryAction",
          icon: <IconAlertTriangle stroke={1.5} size="1.25rem" />,
          color: "#ef4444",
        },
        {
          key: "cancelAdmission",
          title: "Admission Cancellations",
          count: counts.cancelAdmission || 0,
          url: "/dashboard/cancelAdmission/allCancelAdmissionRequest",
          icon: <IconX stroke={1.5} size="1.25rem" />,
          color: "#64748b",
        },
      ];

  const pendingItemsWithCount = taskCategories.filter((item) => item.count > 0);

  return (
    <>
      <Box sx={{ ml: 2, mr: 1 }}>
        <Tooltip title={totalPending > 0 ? `${totalPending} Pending Task${totalPending > 1 ? "s" : ""}` : "Pending Tasks"} arrow>
          <ButtonBase sx={{ borderRadius: "12px", position: "relative" }} onClick={handleToggle}>
            <Avatar
              variant="rounded"
              sx={{
                ...theme.typography.commonAvatar,
                ...theme.typography.mediumAvatar,
                transition: "all .2s ease-in-out",
                background: totalPending > 0 ? "rgba(239, 68, 68, 0.12)" : theme.palette.secondary.light,
                color: totalPending > 0 ? "#dc2626" : theme.palette.secondary.dark,
                border: totalPending > 0 ? "1px solid rgba(239, 68, 68, 0.3)" : "none",
                "&:hover": {
                  background: totalPending > 0 ? "#dc2626" : theme.palette.secondary.dark,
                  color: "#ffffff",
                },
              }}
              ref={anchorRef}
              aria-controls={open ? "menu-list-grow" : undefined}
              aria-haspopup="true"
            >
              <IconBell stroke={1.5} size="1.3rem" />
            </Avatar>
            {totalPending > 0 && (
              <Box
                sx={{
                  position: "absolute",
                  top: -5,
                  right: -6,
                  zIndex: 2,
                }}
              >
                <RedBubble count={totalPending} size="small" />
              </Box>
            )}
          </ButtonBase>
        </Tooltip>
      </Box>

      <Popper
        placement={matchesXs ? "bottom" : "bottom-end"}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [matchesXs ? 5 : 0, 20],
              },
            },
          ],
        }}
        sx={{ zIndex: 1300 }}
      >
        {({ TransitionProps }) => (
          <Transitions position={matchesXs ? "top" : "top-right"} in={open} {...TransitionProps}>
            <Paper
              sx={{
                borderRadius: "14px",
                boxShadow: "0 10px 40px rgba(0, 0, 0, 0.15)",
                border: "1px solid rgba(0, 0, 0, 0.08)",
                overflow: "hidden",
                width: matchesXs ? "92vw" : "380px",
                maxWidth: "420px",
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard border={false} elevation={16} content={false} boxShadow shadow={theme.shadows[16]}>
                  {/* Header */}
                  <Box sx={{ p: 2, pb: 1.5, background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)" }}>
                    <Grid container alignItems="center" justifyContent="space-between">
                      <Grid item>
                        <Stack direction="row" spacing={1.2} alignItems="center">
                          <Typography variant="h4" sx={{ fontWeight: 600, color: "#1e293b", fontSize: "1rem" }}>
                            Pending Tasks
                          </Typography>
                          {totalPending > 0 ? (
                            <Chip
                              size="small"
                              label={`${totalPending} Action${totalPending > 1 ? "s" : ""}`}
                              sx={{
                                height: 22,
                                fontSize: "0.72rem",
                                fontWeight: 700,
                                background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                                color: "#ffffff",
                              }}
                            />
                          ) : (
                            <Chip
                              size="small"
                              label="Clear"
                              color="success"
                              variant="outlined"
                              sx={{ height: 22, fontSize: "0.72rem", fontWeight: 600 }}
                            />
                          )}
                        </Stack>
                      </Grid>
                      <Grid item>
                        <Tooltip title="Refresh Pending Counts" arrow>
                          <IconButton
                            size="small"
                            onClick={refreshPendingTasks}
                            disabled={loading}
                            sx={{ color: "#64748b", "&:hover": { color: "#4f46e5" } }}
                          >
                            <IconRefresh stroke={1.5} size="1.1rem" />
                          </IconButton>
                        </Tooltip>
                      </Grid>
                    </Grid>
                  </Box>
                  <Divider />

                  {/* Body List */}
                  <Box sx={{ maxHeight: "360px", overflowY: "auto", p: 1 }}>
                    {pendingItemsWithCount.length === 0 ? (
                      <Box sx={{ py: 5, px: 3, textAlign: "center" }}>
                        <Avatar
                          sx={{
                            bgcolor: "#dcfce7",
                            color: "#16a34a",
                            width: 52,
                            height: 52,
                            mx: "auto",
                            mb: 1.5,
                          }}
                        >
                          <IconCheck size="1.8rem" stroke={2} />
                        </Avatar>
                        <Typography variant="h5" sx={{ fontWeight: 600, color: "#1e293b", mb: 0.5 }}>
                          All Caught Up!
                        </Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ fontSize: "0.85rem" }}>
                          There are no pending actions requiring your attention.
                        </Typography>
                      </Box>
                    ) : (
                      <List disablePadding>
                        {pendingItemsWithCount.map((item) => (
                          <ListItemButton
                            key={item.key}
                            onClick={() => handleItemClick(item.url)}
                            sx={{
                              borderRadius: "10px",
                              mb: 0.6,
                              py: 1,
                              px: 1.5,
                              transition: "all 0.18s ease-in-out",
                              "&:hover": {
                                bgcolor: "rgba(79, 70, 229, 0.08)",
                                transform: "translateX(3px)",
                              },
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                minWidth: 36,
                                color: item.color,
                              }}
                            >
                              {item.icon}
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#334155" }}>
                                  {item.title}
                                </Typography>
                              }
                            />
                            <RedBubble count={item.count} size="small" />
                          </ListItemButton>
                        ))}
                      </List>
                    )}
                  </Box>

                  {/* Footer */}
                  <Divider />
                  <CardActions sx={{ p: 1.2, justifyContent: "center", bgcolor: "#fafafa" }}>
                    <Typography variant="caption" sx={{ color: "#94a3b8", fontSize: "0.75rem" }}>
                      Auto-refreshes every 40 seconds
                    </Typography>
                  </CardActions>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </>
  );
};

export default PendingTasksSection;
